import { useEffect, useState } from "react";
import { KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const Field = ({ id, label, placeholder, value, onChange }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
      {label}
    </Label>
    <Input
      type="password"
      autoComplete="off"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`system-keys-${id}-input`}
      className="mt-2 h-11 border-slate-300 font-mono text-sm focus-visible:ring-blue-600"
    />
  </div>
);

export const SystemKeysCard = ({ onSaved }) => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ secret_key: "", publishable_key: "", webhook_secret: "" });

  const load = () =>
    api
      .get("/admin/system/stripe-keys")
      .then(({ data }) => setInfo(data))
      .catch((e) => toast.error(errMsg(e)));

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/system/stripe-keys", form);
      toast.success(
        `Saved. Now using your ${data.mode} Stripe account ${data.account_id}` +
          (data.webhook_secret_set ? "." : " — webhook secret still missing.")
      );
      setForm({ secret_key: "", publishable_key: "", webhook_secret: "" });
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const revert = async () => {
    if (!window.confirm("Remove your saved Stripe keys and fall back to the platform keys?"))
      return;
    try {
      await api.delete("/admin/system/stripe-keys");
      toast.success("Reverted to the platform Stripe keys");
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
      data-testid="system-keys-card"
    >
      <p className="flex items-center gap-2 font-display text-base font-bold text-slate-900">
        <KeyRound className="h-4 w-4 text-blue-600" /> System keys — Stripe
      </p>
      <p className="mt-1 text-sm text-slate-600">
        Enter your own Stripe API keys to bill through your Stripe account. Keys are stored on your
        server and never shown again after saving.
      </p>

      {info && (
        <div
          className="mt-4 flex flex-wrap items-center gap-2 text-xs font-semibold"
          data-testid="system-keys-status"
        >
          <span
            className={`rounded-full px-3 py-1 ${
              info.source === "system_keys"
                ? "bg-emerald-50 text-emerald-700"
                : "bg-slate-100 text-slate-700"
            }`}
          >
            {info.source === "system_keys" ? "Using your keys" : "Using platform keys"}
          </span>
          <span
            className={`rounded-full px-3 py-1 ${
              info.mode === "live" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
            }`}
          >
            {info.mode === "live" ? "Live mode" : "Test mode"}
          </span>
          <span className="rounded-full bg-slate-100 px-3 py-1 font-mono text-slate-700">
            {info.secret_key_masked}
          </span>
          <span
            className={`rounded-full px-3 py-1 ${
              info.webhook_secret_masked
                ? "bg-emerald-50 text-emerald-700"
                : "bg-red-50 text-red-700"
            }`}
          >
            {info.webhook_secret_masked ? "Webhook secret set" : "Webhook secret missing"}
          </span>
        </div>
      )}

      <form onSubmit={save} className="mt-5 grid gap-4 border-t border-slate-100 pt-5 sm:grid-cols-3">
        <Field
          id="secret"
          label="Secret key"
          placeholder="sk_live_…"
          value={form.secret_key}
          onChange={(v) => setForm({ ...form, secret_key: v })}
        />
        <Field
          id="publishable"
          label="Publishable key"
          placeholder="pk_live_…"
          value={form.publishable_key}
          onChange={(v) => setForm({ ...form, publishable_key: v })}
        />
        <Field
          id="webhook"
          label="Webhook signing secret"
          placeholder="whsec_…"
          value={form.webhook_secret}
          onChange={(v) => setForm({ ...form, webhook_secret: v })}
        />
        <div className="sm:col-span-3 flex flex-wrap items-center gap-3">
          <Button
            type="submit"
            disabled={busy || !form.secret_key}
            data-testid="system-keys-save-button"
            className="h-11 rounded-full bg-blue-600 px-6 font-semibold hover:bg-blue-700"
          >
            {busy ? "Verifying with Stripe…" : "Save & verify keys"}
          </Button>
          {info?.source === "system_keys" && (
            <Button
              type="button"
              variant="outline"
              onClick={revert}
              data-testid="system-keys-revert-button"
              className="h-11 rounded-full border-slate-300"
            >
              Revert to platform keys
            </Button>
          )}
          <span className="flex items-center gap-1.5 text-xs text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5" /> Verified against Stripe before saving
          </span>
        </div>
      </form>

      <p className="mt-4 text-xs text-slate-500">
        Stripe Dashboard → turn Sandbox/Test mode off → Developers → API keys. Add a webhook
        endpoint at <span className="font-mono">/api/stripe/webhook</span> to get the whsec_ secret.
        Keys beginning with <span className="font-mono">mk_</span> are Sandbox keys and are rejected.
      </p>
    </div>
  );
};
